import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Webpack bundler ile doğrudan import — PUBLIC_URL bağımsız, her ortamda çalışır
import logoWhite from '../../assets/logo-white.webp';
import logoDark  from '../../assets/logo-dark.webp';

// Mirror tek dil (EN) — dil seçici kaldırıldı.
const LANG = 'en';
const IS_TR = false;

// html[data-theme] yöneten hook
function useColorMode() {
    const [colorMode, setColorMode] = useState(() => {
        if (typeof document !== 'undefined') {
            const stored = document.documentElement.getAttribute('data-theme');
            if (stored === 'light' || stored === 'dark') return stored;
        }
        return 'dark';
    });

    useEffect(() => {
        if (typeof document !== 'undefined') {
            document.documentElement.setAttribute('data-theme', colorMode);
        }
    }, [colorMode]);

    return { colorMode, setColorMode };
}

function ColorModeToggle() {
    const { colorMode, setColorMode } = useColorMode();
    const isDark = colorMode === 'dark';

    return (
        <div
            className="rb-theme-toggle"
            onClick={() => setColorMode(isDark ? 'light' : 'dark')}
            role="button"
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
            <div className="rb-theme-toggle__slider">
                {isDark ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                    </svg>
                ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                    </svg>
                )}
            </div>
        </div>
    );
}

function SupportDropdown({ isTr }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const items = [
        {
            label: isTr ? 'Destek' : 'Support',
            href: 'https://www.rabisu.com/panel/submitticket.php',
            icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
                </svg>
            ),
        },
        {
            label: isTr ? 'Rehberler' : 'Tutorials',
            href: `https://www.rabisu.com/en/docs/tutorials`,
            icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                </svg>
            ),
        },
        {
            label: isTr ? 'Bilgi Bankası' : 'Knowledgebase',
            href: `https://www.rabisu.com/en/docs/hosting`,
            icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                </svg>
            ),
        },
        {
            label: 'Blog',
            href: `https://www.rabisu.com/en/blog`,
            icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 11a9 9 0 0 1 9 9" /><path d="M4 4a16 16 0 0 1 16 16" /><circle cx="5" cy="19" r="1" />
                </svg>
            ),
        },
    ];

    return (
        <div className="rb-support-dropdown" ref={ref}>
            <button
                className={`rb-support-dropdown__badge${open ? ' rb-support-dropdown__badge--open' : ''}`}
                onClick={() => setOpen(!open)}
                aria-expanded={open}
                aria-haspopup="true"
            >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 18v-6a9 9 0 0 1 18 0v6" /><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
                </svg>
                <span>{isTr ? 'Destek' : 'Support'}</span>
                <svg className={`rb-support-dropdown__chevron${open ? ' rb-support-dropdown__chevron--open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            {open && (
                <div className="rb-support-dropdown__menu">
                    {items.map((item) => (
                        <a
                            key={item.label}
                            href={item.href}
                            className="rb-support-dropdown__item"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setOpen(false)}
                        >
                            {item.icon}
                            <span>{item.label}</span>
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
}

export function RabisuTopBar() {
    const lang = LANG;
    const isTr = IS_TR;

    return (
        <div className="rb-topbar">
            <div className="rb-container">
                <div className="rb-topbar__content">
                    <div className="rb-topbar__left">
                        <SupportDropdown isTr={isTr} />
                        <a href="https://www.rabisu.com/panel/submitticket.php" className="rb-topbar__link" target="_blank" rel="noopener noreferrer">
                            <span className="rb-topbar__pulse" />
                            <span>Support Online</span>
                        </a>
                    </div>

                    <div className="rb-topbar__right">
                        <ColorModeToggle />
                        {/* Dil seçici kaldırıldı — mirror tek dil (EN) */}
                        <a href="https://mirror.rabisu.com" className="rb-topbar__link" target="_blank" rel="noopener noreferrer">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            <span>Mirror</span>
                        </a>
                        <a href={`https://www.rabisu.com/${lang}/looking-glass`} className="rb-topbar__link" target="_blank" rel="noopener noreferrer">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                            </svg>
                            <span>Looking Glass</span>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function RabisuMainNav() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();
    const lang = LANG;
    const isTr = IS_TR;

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    const navLinks = [
        { href: `https://www.rabisu.com/${lang}`,           label: 'Home' },
        { href: `https://www.rabisu.com/en/domains`,         label: 'Domain' },
        { href: `https://www.rabisu.com/${lang}/hosting`,    label: 'Hosting' },
        { href: `https://www.rabisu.com/${lang}/vps`,        label: 'Server' },
        { href: `https://www.rabisu.com/en/game-server`,     label: 'Game Server' },
        { href: `https://www.rabisu.com/en/who-are-we`,      label: 'About Us' },
    ];

    return (
        <>
            <div className="rb-mainnav">
                <div className="rb-container">
                    <div className="rb-mainnav__inner">

                        <a href="/#/home" className="rb-mainnav__brand">
                            <img className="rb-logo-white" src={logoWhite} alt="Rabisu" width="140" height="45" loading="eager" />
                            <img className="rb-logo-dark"  src={logoDark}  alt="Rabisu" width="140" height="45" loading="eager" />
                        </a>

                        <nav className="rb-mainnav__links">
                            <a href={`https://www.rabisu.com/${lang}`} className="rb-mainnav__link rb-mainnav__link--icon" title="Home">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
                                </svg>
                            </a>
                            {navLinks.slice(1).map(link => (
                                <a key={link.href} href={link.href} className="rb-mainnav__link">{link.label}</a>
                            ))}
                        </nav>

                        <div className="rb-mainnav__actions">
                            <a href="https://www.rabisu.com/panel/clientarea.php" className="rb-mainnav__btn" target="_blank" rel="noopener noreferrer">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                                </svg>
                                <span>Client Panel</span>
                            </a>
                        </div>

                        <button
                            className={`rb-mainnav__hamburger${mobileOpen ? ' rb-mainnav__hamburger--open' : ''}`}
                            onClick={() => setMobileOpen(o => !o)}
                            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={mobileOpen}
                        >
                            {mobileOpen ? (
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            ) : (
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <div
                className={`rb-mobile-overlay${mobileOpen ? ' rb-mobile-overlay--open' : ''}`}
                onClick={() => setMobileOpen(false)}
                aria-hidden="true"
            />

            <nav className={`rb-mobile-menu${mobileOpen ? ' rb-mobile-menu--open' : ''}`} aria-label="Mobile Menu">
                <div className="rb-mobile-menu__header">
                    <a href="/#/home" className="rb-mobile-menu__brand">
                        <img className="rb-logo-white" src={logoWhite} alt="Rabisu" width="120" height="38" loading="lazy" />
                        <img className="rb-logo-dark"  src={logoDark}  alt="Rabisu" width="120" height="38" loading="lazy" />
                    </a>
                    <button className="rb-mobile-menu__close" onClick={() => setMobileOpen(false)} aria-label="Close">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <ul className="rb-mobile-menu__list">
                    {navLinks.map(link => (
                        <li key={link.href}>
                            <a href={link.href} className="rb-mobile-menu__link">{link.label}</a>
                        </li>
                    ))}
                </ul>

                <div className="rb-mobile-menu__footer">
                    <a href="https://www.rabisu.com/panel/clientarea.php" className="rb-mobile-menu__panel-btn" target="_blank" rel="noopener noreferrer">
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                        </svg>
                        Client Panel
                    </a>
                </div>
            </nav>
        </>
    );
}
